import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { showBasicAlert } from "../../../../core/common/useAlert";
import api from "../../../../services/api";
import ComposeGmail from "./ComposeGmail";
import "../../../../style/css/email.css";
import GmailDetail from "./GmailDetail";

export interface Email {
  uid: number;
  from: string;
  to: string;
  cc: string;
  subject: string;
  text: string;
  date: string;
  messageId: string;
  inReplyTo: string | null;
  references: string[];
  isReply: boolean;
  attachments: any[];
  profileImage?: string;
  isRead?: boolean;
  isDraft?: boolean;
}

type ComposeReplyEmail = {
  from: string;
  to: string;
  subject: string;
  messageId: string;
  references: string[];
  isReply: boolean;
};

interface EmailData {
  to: string[];
  cc?: string[];
  subject: string;
  message: string;
  inReplyTo?: string;
  references?: string[];
  replyAll?: boolean;
  attachments?: File[];
}

interface GmailMailViewProps {
  provider: string;
  oauth: any;
  email: any;
}

const GmailMailView: React.FC<GmailMailViewProps> = ({
  provider,
  oauth,
  email,
}) => {
  const [showSelectionMode, setShowSelectionMode] = useState<boolean>(false);
  const [refreshEmail, setRefreshEmail] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [emails, setEmails] = useState<Email[]>([]);
  const [sentEmails, setSentEmails] = useState<Email[]>([]);
  const [filteredEmails, setFilteredEmails] = useState<Email[]>([]);
  const [filteredSentEmails, setFilteredSentEmails] = useState<Email[]>([]);
  const [filteredDraftEmails, setFilteredDraftEmails] = useState<Email[]>([]);
  const [filteredTrashEmails, setFilteredTrashEmails] = useState<Email[]>([]);
  const [filteredJunkEmails, setFilteredJunkEmails] = useState<Email[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [selectedEmails, setSelectedEmails] = useState<number[]>([]);
  const [isForwarding, setIsForwarding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNewEmail, setNewEmail] = useState(0);
  type EmailTab = "inbox" | "sent" | "drafts" | "trash" | "junk";

  const [activeTab, setActiveTab] = useState<EmailTab>("inbox");
  const [pageTokens, setPageTokens] = useState<Record<string, string>>({
    inbox: "",
    sent: "",
    drafts: "",
    trash: "",
    junk: "",
  });
  const [allLoadedEmails, setAllLoadedEmails] = useState<
    Record<string, Email[]>
  >({
    inbox: [],
    sent: [],
    drafts: [],
    trash: [],
    junk: [],
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const tokenRefreshTimer = useRef<NodeJS.Timeout | null>(null);
  const emailsPerPage = 50;

  const signature = localStorage.getItem("signature");
  const [tokens, setTokens] = useState({
    access_token: oauth?.access_token,
  });

  const filterEmails = (emails: Email[], query: string) => {
    if (!query) return emails;
    const lower = query.toLowerCase();
    return emails.filter(
      (e) =>
        e.from.toLowerCase().includes(lower) ||
        e.to.toLowerCase().includes(lower) ||
        e.subject.toLowerCase().includes(lower) ||
        e.text.toLowerCase().includes(lower)
    );
  };

  const refreshAccessToken = async () => {
    if (!signature) {
      console.warn("Authentication signature missing, cannot refresh token.");
      showBasicAlert("Authentication signature missing. Please log in again.");
      return null;
    }

    try {
      const response = await api.post("mailService/refreshValidAccessToken", {
        signature,
        email,
      });
      const newTokens = response.data?.data?.data;
      setTokens({ access_token: newTokens });
      setupTokenRefreshTimer(newTokens.expiry_date);

      return newTokens;
    } catch (error) {
      console.error("Error refreshing access token:", error);
      showBasicAlert("Failed to refresh access token. Please try again.");
      return null;
    }
  };

  const calculateRefreshTime = (expiryDate: number) => {
    const now = Date.now();
    const expiresIn = expiryDate - now;
    const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
    return Math.max(expiresIn - bufferTime, 0);
  };

  const setupTokenRefreshTimer = (expiryDate: number) => {
    if (tokenRefreshTimer.current) {
      clearTimeout(tokenRefreshTimer.current);
      tokenRefreshTimer.current = null;
    }

    if (!expiryDate || isNaN(expiryDate)) {
      console.warn("Invalid expiry date, skipping token refresh timer setup.");
      return;
    }

    const refreshTime = calculateRefreshTime(expiryDate);

    if (refreshTime > 0) {
      tokenRefreshTimer.current = setTimeout(async () => {
        const newTokens = await refreshAccessToken();
        if (!newTokens) {
          tokenRefreshTimer.current = setTimeout(() => {
            setupTokenRefreshTimer(expiryDate);
          }, 30 * 1000); // Retry after 30 seconds
        }
      }, refreshTime);
    } else {
      console.warn("Token already expired or invalid, refreshing immediately.");
      refreshAccessToken();
    }
  };

  useEffect(() => {
    if (oauth?.expiry_date && signature) {
      setupTokenRefreshTimer(oauth.expiry_date);
    } else {
      console.warn(
        "Missing oauth.expiry_date or signature, cannot setup token refresh."
      );
    }

    return () => {
      if (tokenRefreshTimer.current) {
        clearTimeout(tokenRefreshTimer.current);
        tokenRefreshTimer.current = null;
      }
    };
  }, [oauth?.expiry_date, signature]);

  const mapApiEmailToComponentEmail = (apiEmail: any): Email => {
    return {
      uid: apiEmail.uid || apiEmail.messageId,
      from: apiEmail.from || "",
      to: apiEmail.to || "",
      cc: apiEmail.cc || "",
      subject: apiEmail?.subject || "",
      text: apiEmail.text || "",
      date: apiEmail.date || new Date().toISOString(),
      messageId: apiEmail.messageId || "",
      inReplyTo: apiEmail.inReplyTo || null,
      references: apiEmail.references || [],
      isReply: apiEmail.isReply || false,
      attachments: apiEmail.attachments || [],
      isRead: apiEmail.isRead,
      profileImage: apiEmail?.profileImage,
    };
  };

  // put helpers above component/function
  const getEmailKey = (e: Email): string => {
    return (
      (e as any).id ??
      (e as any).gmailId ??
      (e as any).messageId ??
      (e as any).threadId ??
      (e as any).uid
    );
  };

  const mergeEmailsByKey = (fresh: Email[], existing: Email[]): Email[] => {
    const seen = new Set<string>();
    const merged: Email[] = [];
    for (const item of [...fresh, ...existing]) {
      const key = getEmailKey(item);
      if (!key) continue;
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(item);
      }
    }
    return merged;
  };

  const fetchEmails = async (loadMore = false, maintainPage = false) => {
    if (!signature) {
      showBasicAlert("Authentication signature missing.");
      return;
    }

    setIsLoading(refreshEmail === true ? false : true);

    try {
      const endpointMap: Record<EmailTab, string> = {
        inbox: "mailService/getInboxGmails",
        sent: "mailService/getSentGmails",
        drafts: "mailService/getDraftGmails",
        trash: "mailService/getTrashGmails",
        junk: "mailService/getSpamGmails",
      };

      // 👇 Decide if you want to fetch both inbox + sent together
      const tabsToFetch: EmailTab[] =
        activeTab === "inbox" || activeTab === "sent"
          ? (["inbox", "sent"] as EmailTab[])
          : [activeTab];

      // Parallel API requests
      const results = await Promise.all(
        tabsToFetch.map(async (key) => {
          const endpoint = endpointMap[key];
          const payload: any = {
            signature,
            tokens,
            ...(loadMore ? { pageToken: pageTokens[key] } : {}),
          };

          const res = await api.post(endpoint, payload);

          const nextToken = res?.data?.message?.nextPageToken;
          if (nextToken) {
            setPageTokens((prev) => ({ ...prev, [key]: nextToken }));
          }

          const newEmails: Email[] = Array.isArray(res?.data?.message?.messages)
            ? res.data.message.messages.map(mapApiEmailToComponentEmail)
            : [];

          const existing: Email[] = allLoadedEmails[key] ?? [];
          let mergedForTab: Email[];

          if (loadMore) {
            mergedForTab = [...existing, ...newEmails];
          } else if (maintainPage) {
            mergedForTab = mergeEmailsByKey(newEmails, existing);
          } else {
            mergedForTab = newEmails;
          }

          setAllLoadedEmails((prev) => ({ ...prev, [key]: mergedForTab }));

          const filtered = filterEmails(mergedForTab, searchQuery);
          switch (key) {
            case "inbox":
              setFilteredEmails(filtered);
              break;
            case "sent":
              setSentEmails(filtered);
              setFilteredSentEmails(filtered);
              break;
            case "drafts":
              setFilteredDraftEmails(filtered);
              break;
            case "trash":
              setFilteredTrashEmails(filtered);
              break;
            case "junk":
              setFilteredJunkEmails(filtered);
              break;
          }

          return { key, emails: mergedForTab };
        })
      );

      // If you specifically want inbox+sent merged into one list
      if (activeTab === "inbox" || activeTab === "sent") {
        const inboxEmails =
          results.find((r) => r.key === "inbox")?.emails ?? [];
        const sentEmails = results.find((r) => r.key === "sent")?.emails ?? [];

        const merged = mergeEmailsByKey([...inboxEmails, ...sentEmails], []);
        const filtered = filterEmails(merged, searchQuery);

        // 👇 store under inbox since UI might expect that
        setFilteredEmails(filtered);
        setAllLoadedEmails((prev) => ({ ...prev, inboxAndSent: merged }));
      }

      if (!loadMore && !maintainPage) {
        setCurrentPage(1);
      }
    } catch (error: any) {
      console.error(`Failed to fetch ${activeTab} emails:`, error);
      if (error.response?.status === 400) {
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          const newTokens = { access_token: newAccessToken };
          await fetchEmails(loadMore, maintainPage); // retry with fresh token
        }
      }
    } finally {
      setRefreshEmail(false);
      setIsLoading(false);
    }
  };

  // states ke baad, existing useEffects ke paas add:
  useEffect(() => {
    // inbox ke saare loaded emails me se unread gino
    const unread = (allLoadedEmails.inbox || []).reduce((acc, e) => {
      return acc + (e?.isRead ? 0 : 1);
    }, 0);
    setUnreadCount(unread);

    if (isNewEmail == 0) {
      setNewEmail(unread);
    } else {
      if (unread > isNewEmail) {
        const audio = new Audio("/sounds/received_mail.mp3");
        audio.volume = 1.0; // Set volume to maximum
        audio.play().catch((e) => console.warn("Audio play failed:", e));
      }
      setNewEmail(unread);
    }
  }, [allLoadedEmails.inbox]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      // Pass true to maintainPage parameter
      setRefreshEmail(true);
    }, 30000);

    return () => clearInterval(intervalId);
  }, [activeTab]);

  useEffect(() => {
    if (refreshEmail) {
      fetchEmails(false, true); // Refresh but maintain current page
    }
  }, [refreshEmail]);

  useEffect(() => {
    const filterTab = () => {
      setFilteredEmails(filterEmails(allLoadedEmails.inbox, searchQuery));
      setFilteredSentEmails(filterEmails(allLoadedEmails.sent, searchQuery));
      setFilteredDraftEmails(filterEmails(allLoadedEmails.drafts, searchQuery));
      setFilteredTrashEmails(filterEmails(allLoadedEmails.trash, searchQuery));
      setFilteredJunkEmails(filterEmails(allLoadedEmails.junk, searchQuery));
      if (searchQuery) {
        setCurrentPage(1);
      }
    };
    filterTab();
  }, [searchQuery, allLoadedEmails]);

  const handleTabChange = (tab: EmailTab) => {
    setActiveTab(tab);
    setSelectedEmail(null);
    setSelectedEmails([]);
    setSearchQuery("");
    setCurrentPage(1); // Reset page on tab change
  };

  useEffect(() => {
    if (allLoadedEmails[activeTab].length === 0) {
      fetchEmails();
    }
  }, [activeTab]);

  const handleComposeSubmit = async (data: FormData | EmailData) => {
    if (!signature || !tokens) {
      showBasicAlert(
        "Authentication signature or tokens is missing. Please log in again."
      );
      return;
    }

    setIsLoading(true);
    try {
      const formData = data instanceof FormData ? data : new FormData();

      if (!(data instanceof FormData)) {
        let message = data.message;
        let subject = data.subject;

        if (isForwarding && selectedEmail) {
          subject = `Fwd: ${selectedEmail.subject}`;
          message =
            `---------- Forwarded message ----------\n` +
            `From: ${selectedEmail.from}\n` +
            `Date: ${selectedEmail.date}\n` +
            `Subject: ${selectedEmail.subject}\n\n` +
            `${data.message}\n\n` +
            `${selectedEmail.text}`;
        }
        const toArray = Array.isArray(data.to) ? data.to : [data.to];
        const ccArray = Array.isArray(data.cc)
          ? data.cc
          : data.cc
          ? [data.cc]
          : [];

        formData.set("to", JSON.stringify(toArray));
        formData.set("cc", JSON.stringify(ccArray));
        formData.append("subject", subject);
        formData.append("message", message);

        if (data.inReplyTo) {
          formData.append("inReplyTo", data.inReplyTo);
        }
        if (data.references) {
          formData.append("references", JSON.stringify(data.references));
        }
        if (data.replyAll !== undefined) {
          formData.append("replyAll", data.replyAll.toString());
        }
        if (data.attachments) {
          data.attachments.forEach((file) => {
            formData.append("attachments", file);
          });
        }
      }

      if (activeTab === "drafts" && selectedEmail?.uid) {
        formData.append("draftUid", selectedEmail.uid.toString());
      }
      formData.append("signature", signature);
      formData.append("tokens", JSON.stringify(tokens));

      const endpoint =
        activeTab === "drafts" && selectedEmail?.uid
          ? "mailService/sendGmailDraft"
          : "mailService/sendGmailMessage";

      await api.post(endpoint, formData);

      setShowCompose(false);
      setIsForwarding(false);
      fetchEmails();
      showBasicAlert("Email sent successfully!");
    } catch (error) {
      console.error("Error sending email:", error);
      showBasicAlert("Failed to send email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const convertToComposeReplyEmail = (email: Email): ComposeReplyEmail => ({
    from: email.from,
    to: email.to,
    subject: email.subject,
    messageId: email.messageId,
    references: email.references || [],
    isReply: email.isReply || false,
  });

  const toggleEmailSelection = (uid: number) => {
    setSelectedEmails((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  const handleEmailSelection = (email: Email) => {
    if (showSelectionMode) {
      toggleEmailSelection(email.uid);
      return;
    }

    if (activeTab === "drafts") {
      setSelectedEmail({
        ...email,
        isDraft: true,
        attachments: email.attachments || [],
      });
      setShowCompose(true);
      return;
    }

    setSelectedEmail(email);
    if (activeTab === "inbox") {
      markEmailAsRead(email);
    }
  };

  const markEmailAsRead = async (email: Email) => {
    if (!signature || email.isRead) return;

    try {
      await api.post("mailService/markEmailAsRead", {
        signature,
        tokens,
        messageId: email.uid,
      });

      setEmails((prevEmails) =>
        prevEmails.map((e) =>
          e.uid === email.uid ? { ...e, isRead: true } : e
        )
      );
      setFilteredEmails((prevEmails) =>
        prevEmails.map((e) =>
          e.uid === email.uid ? { ...e, isRead: true } : e
        )
      );

      // ✅ count ko safe tarike se decrement
      setUnreadCount((prev) => Math.max(0, prev - 1));

      // ✅ allLoadedEmails.inbox me bhi flag update (taaki #1 effect consistent rahe)
      setAllLoadedEmails((prev) => ({
        ...prev,
        inbox: prev.inbox.map((e) =>
          e.uid === email.uid ? { ...e, isRead: true } : e
        ),
      }));
    } catch (error) {
      console.error("Error marking email as read:", error);
    }
  };

  const moveToBin = async (uids?: number[]) => {
    const emailsToMove = uids || selectedEmails;
    if (!signature || emailsToMove.length === 0) {
      showBasicAlert("Please select at least one email to move to bin.");
      return;
    }

    // ✅ agar inbox me ho, to jitne unread select kiye unka count ghatado (optimistic)
    if (activeTab === "inbox") {
      const unreadBeingMoved = allLoadedEmails.inbox.reduce((acc, e) => {
        if (emailsToMove.includes(e.uid) && !e.isRead) return acc + 1;
        return acc;
      }, 0);
      if (unreadBeingMoved > 0) {
        setUnreadCount((prev) => Math.max(0, prev - unreadBeingMoved));
      }
    }

    setIsLoading(true);
    try {
      await api.post("mailService/moveGmailMessageToTrash", {
        signature,
        tokens,
        uids: String(emailsToMove),
      });

      // ✅ local list se bhi woh mails hata do (optimistic UX), baki fetchEmails rehne do
      setAllLoadedEmails((prev) => ({
        ...prev,
        [activeTab]: prev[activeTab].filter(
          (e) => !emailsToMove.includes(e.uid)
        ),
        inbox:
          activeTab === "inbox"
            ? prev.inbox.filter((e) => !emailsToMove.includes(e.uid))
            : prev.inbox,
      }));

      showBasicAlert(
        `${emailsToMove.length} email(s) moved to bin successfully!`
      );
      setSelectedEmails([]);
      setShowSelectionMode(false);
      fetchEmails();
    } catch (error) {
      console.error("Error moving emails to bin:", error);
      showBasicAlert("Failed to move emails to bin. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("mailService/logoutOAuthController", {
        signature,
        email,
        provider,
      });
      window.location.reload();
    } catch (error) {
      console.error("Logout error:", error);
      showBasicAlert("Error during logout. Please try again.");
    }
  };

  const renderEmailList = () => {
    const data =
      activeTab === "inbox"
        ? filteredEmails
        : activeTab === "sent"
        ? filteredSentEmails
        : activeTab === "drafts"
        ? filteredDraftEmails
        : activeTab === "trash"
        ? filteredTrashEmails
        : filteredJunkEmails;

    const start = (currentPage - 1) * emailsPerPage;
    const end = start + emailsPerPage;
    const list = data.slice(start, end);
    const hasMore = !!pageTokens[activeTab] || end < data.length;

    return (
      <>
        <div className="gmail-pagination">
          <span className="range">Page {currentPage}</span>
          <button
            className="arrow"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          >
            &#x276E;
          </button>
          <button
            className="arrow"
            disabled={!hasMore}
            onClick={async () => {
              // If we're at the end of currently loaded emails and there might be more
              if (currentPage * emailsPerPage >= data.length && hasMore) {
                await fetchEmails(true); // Load more emails
              }
              setCurrentPage((p) => p + 1);
            }}
          >
            &#x276F;
          </button>
        </div>

        {list.length === 0 ? (
          <div>No emails found</div>
        ) : (
          list.map((email) => (
            <div
              className={`gmail-email-item ${
                email.isRead ? "read" : "unread"
              } fade-in`}
              key={email.uid}
              onClick={() => {
                setSelectedEmail(email);
                handleEmailSelection(email);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "10px 12px",
                borderBottom: "1px solid #f1f3f4",
                cursor: "pointer",
              }}
            >
              <div className="avatar me-3">
                <ImageWithBasePath
                  src={
                    email.profileImage || "assets/img/profiles/avatar-01.jpg"
                  }
                  alt="avatar"
                  className="rounded-circle"
                />
              </div>
              <div
                className="email-main flex-grow-1"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    color: "#202124",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {activeTab === "inbox"
                    ? email.from.split("<")[0].replace(/"/g, "").trim()
                    : email.to.split("<")[0].replace(/"/g, "").trim()}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: "0.9rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    gap: "4px",
                  }}
                >
                  <span style={{ fontWeight: 600, color: "#202124" }}>
                    {email.subject.substring(0, 100) || "(No Subject)"}
                  </span>
                  <span
                    style={{
                      color: "#5f6368",
                      flexShrink: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    -- {email.text.substring(0, 30) || ""}
                  </span>
                </div>
              </div>
              <div
                className="email-meta"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  marginLeft: "10px",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    fontSize: "0.8rem",
                    color: "#5f6368",
                    textAlign: "right",
                    lineHeight: "1.2",
                  }}
                >
                  {moment(email.date).format("DD MMM YYYY")}
                  <br />
                  {moment(email.date).format("hh:mm A")}
                </span>
                <button
                  className="btn btn-sm mt-1"
                  style={{
                    color: "#d93025",
                    border: "none",
                    background: "transparent",
                  }}
                  title="Delete Email"
                  onClick={(e) => {
                    e.stopPropagation();
                    moveToBin([email.uid]);
                  }}
                >
                  <i className="ti ti-trash fs-18" />
                </button>
              </div>
            </div>
          ))
        )}
      </>
    );
  };

  return (
    <>
      <div className="gmail-container">
        <div className="gmail-sidebar">
          <button className="compose-btn" onClick={() => setShowCompose(true)}>
            <i className="ti ti-edit" /> Compose
          </button>
          <ul className="gmail-nav">
            {["inbox", "sent", "drafts", "junk", "trash"].map((tab) => (
              <li
                key={tab}
                className={activeTab === tab ? "active" : ""}
                onClick={() =>
                  handleTabChange(
                    tab as "inbox" | "sent" | "drafts" | "junk" | "trash"
                  )
                }
              >
                <i
                  className={`ti ${
                    tab === "inbox"
                      ? "ti-inbox"
                      : tab === "sent"
                      ? "ti-send"
                      : tab === "drafts"
                      ? "ti-file"
                      : tab === "junk"
                      ? "ti-alert-octagon"
                      : "ti-trash"
                  }`}
                />{" "}
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {tab === "inbox" && unreadCount > 0 && (
                  <span className="count">{unreadCount}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className="gmail-content">
          <div className="gmail-toolbar">
            <div className="toolbar-left"></div>
            <div className="toolbar-right">
              <div className={`search-container ${showSearch ? "open" : ""}`}>
                <input
                  type="text"
                  placeholder="Search mail"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                <button
                  className="icon-btn"
                  onClick={() => setShowSearch((prev) => !prev)}
                  title="Search"
                >
                  <i className="ti ti-search" />
                </button>
              </div>
              <button
                className={`icon-btn ${isLoading ? "spinning" : ""}`}
                onClick={() => {
                  fetchEmails();
                }}
                title="Refresh"
              >
                <i className="ti ti-refresh" />
              </button>
              <button
                className="icon-btn logout-btn"
                onClick={handleLogout}
                title="Logout"
              >
                <i className="ti ti-logout" />
              </button>
            </div>
          </div>
          {selectedEmail ? (
            <div className="slide-in">
              <GmailDetail
                email={selectedEmail}
                allEmails={[...emails, ...sentEmails]}
                onBack={() => setSelectedEmail(null)}
                tokens={tokens}
              />
            </div>
          ) : (
            <div className="gmail-email-list">
              {isLoading ? (
                <div className="loading-skeleton">
                  {[...Array(10)].map((_, i) => (
                    <div className="skeleton-email" key={i}>
                      <div className="skeleton-avatar"></div>
                      <div className="skeleton-lines">
                        <div className="skeleton-line short"></div>
                        <div className="skeleton-line"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                renderEmailList()
              )}
            </div>
          )}
        </div>
      </div>
      {showCompose && (
        <div className="compose-popup fade-in">
          <ComposeGmail
            onSubmit={handleComposeSubmit}
            onCancel={() => setShowCompose(false)}
            isLoading={isLoading}
            replyToEmail={
              selectedEmail
                ? convertToComposeReplyEmail(selectedEmail)
                : undefined
            }
            draftEmail={activeTab === "drafts" ? selectedEmail : undefined}
            tokens={tokens}
          />
        </div>
      )}
    </>
  );
};

export default GmailMailView;
